/**
 * Notification Context - TEMPORARILY DISABLED
 *
 * Notifications are not part of Workshop 4 requirements.
 * This context provides empty stubs to prevent import errors.
 * Will be reimplemented when Workshop 4 notification requirements are defined.
 */

import React, { createContext, useState, useCallback, ReactNode } from 'react'
import { Notification } from '@/types'

interface NotificationContextValue {
  notifications: Notification[]
  unreadCount: number
  urgentCount: number
  isLoading: boolean
  markAsRead: (notificationId: string) => void
  markAllAsRead: () => void
  dismissNotification: (notificationId: string) => void
  addNotification: (notification: Notification) => void
  refreshNotifications: () => void
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined)

interface NotificationProviderProps {
  children: ReactNode
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications] = useState<Notification[]>([])
  const [unreadCount] = useState(0)
  const [urgentCount] = useState(0)
  const [isLoading] = useState(false)

  /**
   * Mark notification as read (stub)
   */
  const markAsRead = useCallback((_notificationId: string) => {
    // Stub - notifications disabled for Workshop 4
  }, [])

  /**
   * Mark all notifications as read (stub)
   */
  const markAllAsRead = useCallback(() => {
    // Stub - notifications disabled for Workshop 4
  }, [])

  /**
   * Dismiss/archive notification (stub)
   */
  const dismissNotification = useCallback((_notificationId: string) => {
    // Stub - notifications disabled for Workshop 4
  }, [])

  /**
   * Add new notification (stub)
   */
  const addNotification = useCallback((_notification: Notification) => {
    // Stub - notifications disabled for Workshop 4
  }, [])

  /**
   * Refresh notifications from data source (stub)
   */
  const refreshNotifications = useCallback(() => {
    // Stub - notifications disabled for Workshop 4
  }, [])

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    urgentCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    addNotification,
    refreshNotifications,
  }

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
}

/**
 * Hook to use notification context
 */
export const useNotificationContext = () => {
  const context = React.useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider')
  }
  return context
}

export default NotificationContext
